import React from 'react';
import { CatalogCompanyType, CatalogItem } from '@/shared/types/catalog';
import Link from 'next/link';
import CatalogItemProductionCost from '../CatalogItemProductionCost';
import { useQueryParams } from '@/shared/hooks';
import { ParamKey } from '@/shared/types/url-params';
import './index.less';
import { isEmpty } from 'lodash-es';
import Image from 'next/image';

interface IProps {
  product: CatalogItem;
  onClick?: () => void;
  showFactoryDetails?: boolean;
  figcaption?: string;
}

const CatalogItemMinifiedCard = ({ product, onClick, showFactoryDetails, figcaption }: IProps) => {
  const { queryParams } = useQueryParams<{}>();

  const searchKey = queryParams.get(ParamKey.SEARCH);
  const supplier = product.catalogCompany;
  const isServices = supplier?.type === CatalogCompanyType.SPECIALIST;
  const showMatchedFields = !isEmpty(searchKey) && !isEmpty(product?.matched_fields);

  if (!product?.id) return <div />;

  const firstProductImage = product.images?.[0];
  const secondProductImage = product.images?.[1];
  const moq = product?.meta?.moqInfo?.[0]?.qty || 'Inquire';

  const supplierProfileHref = `/sourcing/suppliers/${supplier?.handle}`;
  const productDetailsHref = `/sourcing/products/${product.handle}`;

  const supplierName = supplier?.name;

  return (
    <div className="catalog-item-minified-card">
      {showFactoryDetails && (
        <Link className="factory-details" href={supplierProfileHref}>
          {supplier?.avatarUrl && (
            <>
              <Image
                className="left"
                src={supplier?.avatarUrl}
                height={42}
                width={42}
                alt={supplierName}
                loading="lazy"
              />
            </>
          )}
          <div className="right">
            <div className="header">
              <h2 className="title">{supplierName}</h2>
            </div>
          </div>
        </Link>
      )}
      <Link
        className="image-container"
        href={productDetailsHref}
        onClick={() => {
          onClick?.();
        }}
      >
        <Image src={firstProductImage} alt={product?.name} width={190} height={196} loading="lazy" />
        {secondProductImage && (
          <figure>
            <Image
              className="hover-image"
              src={secondProductImage}
              alt={product?.name}
              width={190}
              height={196}
              loading="lazy"
            />
            {figcaption && <figcaption>{figcaption}</figcaption>}
          </figure>
        )}
      </Link>
      <Link
        className="text"
        href={productDetailsHref}
        onClick={() => {
          onClick?.();
        }}
      >
        <div className="top">
          <h2 className="title">{product.name}</h2>
          <CatalogItemProductionCost product={product} isServices={isServices} />
          {!isServices && !showFactoryDetails && (
            <div className="customizable">
              <Image
                src="https://static.pietrastudio.com/public/file_uploads/d934acee9845a9889c15fdaeb29ce7e3.svg"
                width={12}
                height={12}
                alt="customizable icon"
                loading="lazy"
              />
            </div>
          )}
        </div>
      </Link>
      <div className="cta-container">
        <Link
          className="request-sample-btn btn"
          href={productDetailsHref}
          onClick={() => {
            onClick?.();
          }}
        >
          <img
            src="https://static.pietrastudio.com/public/file_uploads/b92e80b1811f735c4ef68f2f16e1a67f.svg"
            width={12}
            height={12}
            alt="add to cart icon"
            loading="lazy"
          />
          <span className={isServices ? 'request-service' : 'order-sample'}></span>
        </Link>
        {showFactoryDetails && (
          <Link className="request-sample-btn btn" href={supplierProfileHref}>
            <img
              src="https://static.pietrastudio.com/public/file_uploads/895fc2944a2c52a5c9166bb5ffff94a3.svg"
              width={12}
              height={12}
              alt="chat icon"
              loading="lazy"
            />
            <span data-text="Contact"></span>
          </Link>
        )}
      </div>
    </div>
  );
};

export default CatalogItemMinifiedCard;
